class CustomShop {
  constructor(container) {
    this.container = container;
    this.sliderEl = this.container.querySelector('.cstm-collection-tabs-slider');
    this.productGridEl = this.container.querySelector('#cstm-product-grid-container');
    this.loaderEl = this.container.querySelector('#cstm-infinite-scroll-loader');
    this.gridOverlayEl = this.container.querySelector('#cstm-product-grid-overlay');
    this.headerEl = this.container.querySelector('.cstm-product-table-header');
    this.toolbarEl = this.container.querySelector('#cstm-product-toolbar');
    this.productCountEl = this.container.querySelector('#cstm-product-count');
    this.searchInputEl = this.container.querySelector('#cstm-product-search-input');
    this.filterDrawerContainer = this.container.querySelector('#cstm-filter-drawer-container');
    this.filterButton = this.container.querySelector('#cstm-product-filter-button');
    this.paginationEl = this.container.querySelector('#cstm-pagination-container');
    this.sentinelEl = this.container.querySelector('#cstm-infinite-scroll-sentinel');
    this.serverPageSize = 50;

    this.currentCollection = this.container.dataset.initialCollectionHandle || null;
    this.currentFilters = '';
    this.sortOrder = this.container.dataset.sortOrder || 'default';
    this.loadMode = this.container.dataset.productLoadMode || 'infinite';
    this.productsPerPage = parseInt(this.container.dataset.productsPerPage, 10) || 20;
    this.currentLoadId = 0;
    this.currentPage = 1;
    this.renderedCount = 0;
    this.activeSearchResults = null;
    this.searchRequestId = 0;
    this.infiniteObserver = null;
    this.isAppending = false;
    this.collectionState = this._buildInitialCollectionState();
    this.customerLoggedIn =
      this.container.dataset.customerLoggedIn === 'true' || this.productGridEl?.dataset.customerLoggedIn === 'true';

    this._initSlider();
    this._initToolbar();
    this._initFilterDrawer();
    this._initPagination();
    this._initAddToCart();
    this._initQuantityValidation();
    this._initTagToggle();
    this._initSpecDrawer();
    this._initViewMore();
    this._initModelFilterExtras();
    this._buildProductSkeletonOverlay();

    if (this.toolbarEl) this.toolbarEl.style.display = 'flex';

    const initialHandle =
      this.currentCollection || this.container.querySelector('.cstm-collection-tab')?.dataset.collectionHandle || null;

    if (initialHandle) {
      this.currentCollection = initialHandle;
      this._setActiveTab(initialHandle);
      this._loadFiltersForCollection(initialHandle);
      this._initializeProductView();
    } else {
      this._renderEmptyState();
    }
  }

  _buildProductSkeletonOverlay(rowCount = this._getSkeletonRowCount()) {
    if (!this.gridOverlayEl) return;

    const skeletonCount = Math.max(rowCount, 1);
    const skeletonRows = Array.from({ length: skeletonCount }, () => this._buildSkeletonRowTemplate()).join('');

    this.gridOverlayEl.innerHTML = `
      <div class="cstm-product-skeleton-list" aria-hidden="true">
        ${skeletonRows}
      </div>
    `;
  }

  _getSkeletonRowCount() {
    const currentRows = this.productGridEl ? this.productGridEl.querySelectorAll(':scope > .cstm-product-row').length : 0;

    if (currentRows > 0) {
      return currentRows;
    }

    return Math.max(this.productsPerPage, 4);
  }

  _buildSkeletonRowTemplate() {
    const customerColumns = this.customerLoggedIn
      ? `
        <span class="cstm-product-skeleton-block cstm-product-skeleton-block--qty"></span>
        <span class="cstm-product-skeleton-block cstm-product-skeleton-block--action"></span>
      `
      : '';

    return `
      <div class="cstm-product-row cstm-product-row--skeleton tw-py-6 tw-px-4">
        <div class="cstm-product-skeleton-main">
          <span class="cstm-product-skeleton-block cstm-product-skeleton-block--image"></span>
          <div class="cstm-product-skeleton-copy">
            <span class="cstm-product-skeleton-block cstm-product-skeleton-block--title"></span>
            <span class="cstm-product-skeleton-block cstm-product-skeleton-block--meta"></span>
          </div>
        </div>
        <span class="cstm-product-skeleton-block cstm-product-skeleton-block--info"></span>
        <span class="cstm-product-skeleton-block cstm-product-skeleton-block--price"></span>
        <span class="cstm-product-skeleton-block cstm-product-skeleton-block--stock"></span>
        ${customerColumns}
      </div>
    `;
  }

  _createEmptyCollectionState() {
    return {
      products: [],
      totalCount: 0,
      totalServerPages: null,
      nextServerPage: 1,
      hasMoreServerPages: true,
      isFetching: false,
      fetchPromise: null,
    };
  }

  _buildInitialCollectionState() {
    const state = this._createEmptyCollectionState();
    const initialProducts = Array.from(this.productGridEl?.querySelectorAll('.cstm-product-row') || []).map((row) =>
      this._parseProductRow(row)
    );
    const initialTotalProducts = parseInt(this.productGridEl?.dataset.initialTotalProducts || '0', 10) || initialProducts.length;

    state.products = initialProducts;
    state.totalCount = initialTotalProducts;
    state.totalServerPages = initialTotalProducts > 0 ? Math.ceil(initialTotalProducts / this.serverPageSize) : 0;
    state.nextServerPage = initialProducts.length > 0 ? 1 : 1;
    state.hasMoreServerPages = initialTotalProducts > initialProducts.length;

    return state;
  }

  _initSlider() {
    if (!this.sliderEl) return;
    if (this.sliderEl.swiper) this.sliderEl.swiper.destroy(true, true);
    const mobileSlides = parseFloat(this.sliderEl.dataset.mobileSlides) || 3;
    const desktopSlides = parseFloat(this.sliderEl.dataset.desktopSlides) || 5;
    const mobileViewportOffset = 24;
    const sliderShell = this.sliderEl.closest('.cstm-collection-tabs-shell');
    new Swiper(this.sliderEl, {
      slidesPerView: mobileSlides,
      spaceBetween: 6,
      slidesOffsetBefore: mobileViewportOffset,
      slidesOffsetAfter: mobileViewportOffset,
      grabCursor: true,
      navigation: {
        nextEl: sliderShell?.querySelector('.swiper-button-next'),
        prevEl: sliderShell?.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        750: {
          spaceBetween: 8,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
        1024: {
          slidesPerView: desktopSlides,
          spaceBetween: 8,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
      },
    });

    this._scrollActiveTabIntoView();
  }

  _scrollActiveTabIntoView() {
    if (!this.sliderEl || !this.sliderEl.swiper) return;

    const tabs = Array.from(this.container.querySelectorAll('.cstm-collection-tab'));
    const activeIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));
    if (activeIndex >= 0) {
      this.sliderEl.swiper.slideTo(activeIndex);
    }
  }

  _initializeProductView() {
    const hasInitialProducts = this.collectionState.products.length > 0;

    if (!hasInitialProducts && this.collectionState.totalCount === 0) {
      this._renderEmptyState();
      return;
    }

    this.currentPage = 1;
    this.renderedCount = this.collectionState.products.length;
    this._updateProductCount(this.collectionState);

    if (this.headerEl) {
      this.headerEl.style.display = hasInitialProducts ? 'grid' : 'none';
    }
    if (this.toolbarEl) {
      this.toolbarEl.style.display = 'flex';
    }

    if (this.loadMode === 'pagination') {
      this._renderPagination(this.collectionState);
      this._toggleInfiniteElements(false);
      return;
    }

    this._togglePagination(false);
    this._toggleInfiniteElements(this._hasMoreInfiniteResults(this.collectionState));
  }

  _initPagination() {
    if (!this.paginationEl) return;
    this.paginationEl.addEventListener('click', (event) => this._handlePaginationClick(event));
  }

  _initAddToCart() {
    this.productGridEl.addEventListener('click', (event) => this._handleAddToCartClick(event));
  }

  async _handleAddToCartClick(event) {
    const button = event.target.closest('.cstm-add-to-cart-button');
    if (!button) return;

    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const productRow = button.closest('.cstm-product-row');
    const qtyInput = productRow.querySelector('.cstm-product-row__qty-input');
    const quantity = parseInt(qtyInput.value, 10);

    if (quantity <= 0) {
      alert('Please enter a quantity greater than 0.');
      return;
    }

    const loader = button.querySelector('.cstm-add-to-cart-button__loader');
    const icon = button.querySelector('.cstm-add-to-cart-button__icon');
    
    button.disabled = true;
    if (loader) loader.style.display = 'flex';
    if (icon) icon.style.display = 'none';

    try {
      const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: quantity }],
        sections: sectionsToRender.join(','),
        sections_url: window.location.pathname
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });
      
      const parsedState = await response.json();

      if (response.status !== 200) {
        let errorMessage = parsedState.description || parsedState.message || 'Could not add item to cart.';
        alert(errorMessage);
        location.reload();
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const cartIconBubble = document.getElementById('cart-icon-bubble');

      if (cartIconBubble && parsedState.sections['cart-icon-bubble']) {
         cartIconBubble.innerHTML = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section').innerHTML;
      }
      
      if (cartDrawer && parsedState.sections['cart-drawer']) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = parsedState.sections['cart-drawer'];
        
        const newDrawerContent = tempDiv.querySelector('cart-drawer');
        if (newDrawerContent) {
          cartDrawer.innerHTML = newDrawerContent.innerHTML;
          
          if (cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }
        }
        
        cartDrawer.open();
      }
      
      qtyInput.value = '0';

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('An error occurred. Please try again.');
    } finally {
      button.disabled = false;
      if (loader) loader.style.display = 'none';
      if (icon) icon.style.display = 'block';
    }
  }

  async _loadCollection(handle, { resetSearch = false, scrollToResults = true } = {}) {
    this.currentLoadId += 1;
    const loadId = this.currentLoadId;
    const state = this._createEmptyCollectionState();

    this.collectionState = state;
    this.currentCollection = handle;
    this.currentPage = 1;
    this.renderedCount = 0;

    if (resetSearch) {
      this.searchRequestId += 1;
      this.activeSearchResults = null;
      if (this.searchInputEl) this.searchInputEl.value = '';
    }

    this._disconnectInfiniteObserver();
    this._setActiveTab(handle);
    this._togglePagination(false);
    this._toggleInfiniteElements(false);
    this._clearProductGrid();

    if (scrollToResults) {
      this._scrollToResults();
    }

    this._setLoadingState({ overlay: true, loader: false });

    try {
      await this._ensureProductsLoaded(this.productsPerPage, loadId, state);
      if (this.currentLoadId !== loadId || this.collectionState !== state) return;

      if (this.loadMode === 'pagination') {
        await this._renderCurrentPage(loadId, state);
      } else {
        await this._renderNextInfiniteBatch(loadId, { reset: true }, state);
      }
    } catch (error) {
      console.error('Error fetching collection:', error);
      if (this.currentLoadId === loadId && this.collectionState === state) {
        this._renderEmptyState();
      }
    } finally {
      if (this.currentLoadId === loadId && this.collectionState === state) {
        this._setLoadingState({ overlay: false, loader: false });
      }
    }
  }

  _buildCollectionUrl(handle, page) {
    const params = new URLSearchParams({ section_id: 'custom-product-grid', page: String(page) });
    const filterParams = new URLSearchParams(this.currentFilters);
    filterParams.forEach((value, key) => params.append(key, value));

    if (this.sortOrder === 'best-selling') {
      params.append('sort_by', 'best-selling');
    }

    return `/collections/${handle}?${params.toString()}`;
  }

  _parseCollectionResponse(htmlText) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlText;

    const wrapper = tempDiv.querySelector('[data-cstm-product-grid]');
    const rows = Array.from(tempDiv.querySelectorAll('.cstm-product-row'));

    return {
      products: rows.map((row) => this._parseProductRow(row)),
      totalCount: parseInt(wrapper?.dataset.totalProducts || rows.length, 10) || 0,
      totalPages: parseInt(wrapper?.dataset.totalPages || '0', 10) || 0,
    };
  }

  async _ensureProductsLoaded(minimumCount, loadId = this.currentLoadId, state = this.collectionState) {
    while (
      this.currentLoadId === loadId &&
      this.collectionState === state &&
      state.products.length < minimumCount &&
      state.hasMoreServerPages
    ) {
      await this._fetchNextServerPage(loadId, state);
    }
  }

  async _ensureAllProductsLoaded(loadId = this.currentLoadId, state = this.collectionState) {
    while (this.currentLoadId === loadId && this.collectionState === state && state.hasMoreServerPages) {
      await this._fetchNextServerPage(loadId, state);
    }
  }

  async _fetchNextServerPage(loadId = this.currentLoadId, state = this.collectionState) {
    if (!this.currentCollection || !state.hasMoreServerPages) return;

    if (state.fetchPromise) {
      await state.fetchPromise;
      return;
    }

    const page = state.nextServerPage;
    state.isFetching = true;
    this._updateLoaderVisibility();

    const fetchPromise = (async () => {
      const response = await fetch(this._buildCollectionUrl(this.currentCollection, page));
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const htmlText = await response.text();
      if (this.currentLoadId !== loadId || this.collectionState !== state) return;

      const parsed = this._parseCollectionResponse(htmlText);

      if (page === 1 || !state.totalCount) {
        state.totalCount = parsed.totalCount;
      }
      if (page === 1 || !state.totalServerPages) {
        state.totalServerPages = parsed.totalPages;
      }

      if (parsed.products.length > 0) {
        state.products = this._mergeProducts(state.products, parsed.products);
        state.nextServerPage += 1;
      }

      const totalCount = parsed.totalCount || state.totalCount || 0;
      const totalPages = parsed.totalPages || state.totalServerPages || 0;
      const fetchedAllByCount = totalCount > 0 && state.products.length >= totalCount;
      const fetchedAllByPage = totalPages > 0 && page >= totalPages;

      state.hasMoreServerPages = parsed.products.length > 0 && !fetchedAllByCount && !fetchedAllByPage;
      this._updateProductCount(state);
    })();

    state.fetchPromise = fetchPromise;

    try {
      await fetchPromise;
    } catch (error) {
      state.hasMoreServerPages = false;
      throw error;
    } finally {
      if (state.fetchPromise === fetchPromise) {
        state.fetchPromise = null;
      }
      state.isFetching = false;
      this._updateLoaderVisibility();
    }
  }

  _setActiveTab(handle) {
    this.container.querySelectorAll('.cstm-collection-tab').forEach((tab) => {
      tab.classList.toggle('is-active', tab.dataset.collectionHandle === handle);
      if (tab.dataset.collectionHandle === handle) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    });

    this._scrollActiveTabIntoView();
  }

  _mergeProducts(existingProducts, newProducts) {
    const productsById = new Map();

    existingProducts.forEach((product) => {
      productsById.set(product.id || product.sku || product.outerHTML, product);
    });

    newProducts.forEach((product) => {
      const key = product.id || product.sku || product.outerHTML;
      if (!productsById.has(key)) {
        productsById.set(key, product);
      }
    });

    return Array.from(productsById.values());
  }

  _getProductSource(state = this.collectionState) {
    return this.activeSearchResults || state.products;
  }

  _getTotalItems(state = this.collectionState) {
    if (this.activeSearchResults) {
      return this.activeSearchResults.length;
    }

    return state.totalCount || state.products.length;
  }

  _getTotalPages(state = this.collectionState) {
    const totalItems = this._getTotalItems(state);
    if (totalItems <= 0) return 0;

    return Math.ceil(totalItems / this.productsPerPage);
  }

  _updateProductCount(state = this.collectionState) {
    if (!this.productCountEl) return;

    this.productCountEl.textContent = this._getTotalItems(state);
  }

  _clearProductGrid() {
    if (!this.productGridEl) return;

    this.productGridEl.innerHTML = '';
  }

  _renderProductList(products, { append = false } = {}) {
    if (!this.productGridEl) return;

    if (!append) {
      this.productGridEl.innerHTML = '';
    }

    if (products.length === 0 && !append) {
      this._renderEmptyState();
      return;
    }

    if (products.length > 0 && this.headerEl) {
      this.headerEl.style.display = 'grid';
    }
    if (this.toolbarEl) {
      this.toolbarEl.style.display = 'flex';
    }

    const html = products.map((product) => product.outerHTML).join('');
    if (html) {
      this.productGridEl.insertAdjacentHTML('beforeend', html);
    }
  }

  _renderEmptyState(message = 'No products found.') {
    this._clearProductGrid();

    if (this.headerEl) {
      this.headerEl.style.display = 'none';
    }
    if (this.toolbarEl) {
      this.toolbarEl.style.display = 'flex';
    }
    if (this.productCountEl) {
      this.productCountEl.textContent = '0';
    }
    if (this.productGridEl) {
      this.productGridEl.innerHTML = `<p class="tw-text-center" style="padding-block: 3rem;">${message}</p>`;
    }

    this._togglePagination(false);
    this._toggleInfiniteElements(false);
  }

  async _renderCurrentPage(loadId = this.currentLoadId, state = this.collectionState) {
    if (this.currentLoadId !== loadId || this.collectionState !== state) return;

    let totalPages = this._getTotalPages(state);
    if (totalPages === 0) {
      this._renderEmptyState();
      return;
    }

    this.currentPage = Math.min(Math.max(this.currentPage, 1), totalPages);

    if (!this.activeSearchResults) {
      await this._ensureProductsLoaded(this.currentPage * this.productsPerPage, loadId, state);
      if (this.currentLoadId !== loadId || this.collectionState !== state) return;
      totalPages = this._getTotalPages(state);
      this.currentPage = Math.min(Math.max(this.currentPage, 1), totalPages || 1);
    }

    const products = this._getProductSource(state);
    const start = (this.currentPage - 1) * this.productsPerPage;
    const end = start + this.productsPerPage;

    this._renderProductList(products.slice(start, end));
    this.renderedCount = Math.min(end, products.length);
    this._updateProductCount(state);
    this._renderPagination(state);
    this._toggleInfiniteElements(false);
  }

  async _renderNextInfiniteBatch(loadId = this.currentLoadId, { reset = false } = {}, state = this.collectionState) {
    if (this.loadMode !== 'infinite') return;
    if (this.isAppending || this.currentLoadId !== loadId || this.collectionState !== state) return;

    this.isAppending = true;

    try {
      if (reset) {
        this.renderedCount = 0;
        this._clearProductGrid();
      }

      const targetCount = this.renderedCount + this.productsPerPage;
      if (!this.activeSearchResults) {
        await this._ensureProductsLoaded(targetCount, loadId, state);
      }
      if (this.currentLoadId !== loadId || this.collectionState !== state) return;

      const products = this._getProductSource(state);
      const nextProducts = products.slice(this.renderedCount, this.renderedCount + this.productsPerPage);

      if (this.renderedCount === 0 && nextProducts.length === 0) {
        this._renderEmptyState();
        return;
      }

      if (nextProducts.length > 0) {
        this._renderProductList(nextProducts, { append: this.renderedCount > 0 });
        this.renderedCount += nextProducts.length;
      }

      this._updateProductCount(state);
      this._togglePagination(false);
      this._toggleInfiniteElements(this._hasMoreInfiniteResults(state));
    } finally {
      this.isAppending = false;
      this._updateLoaderVisibility();
    }
  }

  _hasMoreInfiniteResults(state = this.collectionState) {
    const products = this._getProductSource(state);

    if (this.renderedCount < products.length) {
      return true;
    }

    return !this.activeSearchResults && state.hasMoreServerPages;
  }

  _renderPagination(state = this.collectionState) {
    if (!this.paginationEl || this.loadMode !== 'pagination') {
      this._togglePagination(false);
      return;
    }

    const totalPages = this._getTotalPages(state);
    if (totalPages <= 1) {
      this.paginationEl.innerHTML = '';
      this._togglePagination(false);
      return;
    }

    const parts = this._buildPaginationParts(totalPages, this.currentPage);
    const pageButtons = parts.map((part) => {
      if (typeof part !== 'number') {
        return '<span class="cstm-pagination__ellipsis" aria-hidden="true">...</span>';
      }

      return `
        <button
          type="button"
          class="cstm-pagination__button cstm-pagination__button--page${part === this.currentPage ? ' is-active' : ''}"
          data-page="${part}"
          ${part === this.currentPage ? 'aria-current="page"' : ''}
          aria-label="Go to page ${part}"
        >
          ${part}
        </button>
      `;
    }).join('');

    this.paginationEl.innerHTML = `
      <nav class="cstm-pagination" aria-label="Pagination">
        <button
          type="button"
          class="cstm-pagination__button cstm-pagination__button--nav"
          data-page="${this.currentPage - 1}"
          aria-label="Previous page"
          ${this.currentPage === 1 ? 'disabled' : ''}
        >
          <svg class="cstm-pagination__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M10 3.5L5.5 8L10 12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="visually-hidden">Previous page</span>
        </button>
        ${pageButtons}
        <button
          type="button"
          class="cstm-pagination__button cstm-pagination__button--nav"
          data-page="${this.currentPage + 1}"
          aria-label="Next page"
          ${this.currentPage === totalPages ? 'disabled' : ''}
        >
          <svg class="cstm-pagination__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="visually-hidden">Next page</span>
        </button>
      </nav>
    `;

    this._togglePagination(true);
  }

  _buildPaginationParts(totalPages, currentPage) {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5, 'ellipsis-end'];
    }

    if (currentPage >= totalPages - 2) {
      return ['ellipsis-start', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return ['ellipsis-start', currentPage - 1, currentPage, currentPage + 1, 'ellipsis-end'];
  }

  _handlePaginationClick(event) {
    const button = event.target.closest('[data-page]');
    if (!button || button.disabled) return;

    const page = parseInt(button.dataset.page || '', 10);
    if (Number.isNaN(page)) return;

    this._goToPage(page);
  }

  async _goToPage(page) {
    if (this.loadMode !== 'pagination') return;

    const state = this.collectionState;
    const totalPages = this._getTotalPages(state);
    if (totalPages <= 0) return;

    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (nextPage === this.currentPage) return;

    const loadingStart = performance.now();
    const minimumLoadingMs = 220;

    this.currentPage = nextPage;
    this._setLoadingState({ overlay: true, loader: false });

    // Ensure the browser paints the loading skeleton before rendering new page rows.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    try {
      await this._renderCurrentPage(this.currentLoadId, state);
      this._scrollToResults();
    } finally {
      if (this.collectionState === state) {
        const elapsed = performance.now() - loadingStart;
        if (elapsed < minimumLoadingMs) {
          await new Promise((resolve) => setTimeout(resolve, minimumLoadingMs - elapsed));
        }
        this._setLoadingState({ overlay: false, loader: false });
      }
    }
  }

  _togglePagination(isVisible) {
    if (!this.paginationEl) return;

    this.paginationEl.style.display = isVisible ? 'block' : 'none';
  }

  _toggleInfiniteElements(isVisible) {
    const shouldShow = this.loadMode === 'infinite' && isVisible;

    if (this.sentinelEl) {
      this.sentinelEl.style.display = shouldShow ? 'block' : 'none';
    }

    if (shouldShow) {
      this._ensureInfiniteObserver();
    } else {
      this._disconnectInfiniteObserver();
      if (this.loaderEl) {
        this.loaderEl.style.display = 'none';
      }
    }
  }

  _ensureInfiniteObserver() {
    if (!this.sentinelEl || this.infiniteObserver || this.loadMode !== 'infinite') return;

    this.infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._renderNextInfiniteBatch();
        }
      },
      { rootMargin: '300px 0px' }
    );

    this.infiniteObserver.observe(this.sentinelEl);
  }

  _disconnectInfiniteObserver() {
    if (!this.infiniteObserver) return;

    this.infiniteObserver.disconnect();
    this.infiniteObserver = null;
  }

  _updateLoaderVisibility() {
    if (!this.loaderEl) return;

    const shouldShow =
      this.loadMode === 'infinite' &&
      !this.activeSearchResults &&
      this.collectionState.isFetching &&
      this.renderedCount > 0;

    this.loaderEl.style.display = shouldShow ? 'block' : 'none';
  }

  _setLoadingState({ overlay = false, loader = false }) {
    if (overlay) {
      this._buildProductSkeletonOverlay(this._getSkeletonRowCount());
      this._syncSkeletonRowHeight();
    }

    if (this.gridOverlayEl) {
      this.gridOverlayEl.style.display = overlay ? 'block' : 'none';
    }
    if (this.productGridEl) {
      this.productGridEl.classList.toggle('is-loading', overlay);
    }
    if (this.loaderEl) {
      this.loaderEl.style.display = loader ? 'block' : 'none';
    }
  }

  _syncSkeletonRowHeight() {
    if (!this.gridOverlayEl) return;

    const firstRow = this.productGridEl?.querySelector(':scope > .cstm-product-row');
    const measuredHeight = firstRow ? Math.round(firstRow.getBoundingClientRect().height) : 0;
    const fallbackHeight = 81;
    const rowHeight = measuredHeight > 0 ? measuredHeight : fallbackHeight;

    this.gridOverlayEl.style.setProperty('--cstm-skeleton-row-height', `${rowHeight}px`);
  }

  _scrollToResults() {
    const target = this.toolbarEl || this.productGridEl;
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _parseProductRow(rowElement) {
    const product = {
        id: rowElement.querySelector('[data-popup-trigger-for]')?.dataset.popupTriggerFor || '',
        title: rowElement.querySelector('h2')?.textContent.trim().toLowerCase() || '',
        sku: rowElement.querySelector('[data-product-sku]')?.textContent.trim().toLowerCase() || '',
        outerHTML: rowElement.outerHTML,
    };
    return product;
  }

  _initToolbar() {
    if (!this.searchInputEl) return;

    this.searchInputEl.addEventListener('input', (event) => this._handleSearchInput(event));
  }

  async _handleSearchInput(event) {
    if (!this.currentCollection) return;

    const state = this.collectionState;
    const loadId = this.currentLoadId;
    const searchTerm = event.target.value.trim().toLowerCase();
    const requestId = ++this.searchRequestId;

    this._setLoadingState({ overlay: true, loader: false });

    try {
      if (!searchTerm) {
        this.activeSearchResults = null;
        this.currentPage = 1;
        this.renderedCount = 0;

        if (this.loadMode === 'pagination') {
          await this._renderCurrentPage(loadId, state);
        } else {
          await this._renderNextInfiniteBatch(loadId, { reset: true }, state);
        }
        return;
      }

      await this._ensureAllProductsLoaded(loadId, state);
      if (this.searchRequestId !== requestId || this.currentLoadId !== loadId || this.collectionState !== state) return;

      const keywords = searchTerm.split(' ').filter((keyword) => keyword !== '');
      this.activeSearchResults = state.products.filter((product) => {
        const skuMatch = product.sku.includes(searchTerm);
        const titleMatch = keywords.every((keyword) => product.title.includes(keyword));
        return titleMatch || skuMatch;
      });

      this.currentPage = 1;
      this.renderedCount = 0;

      if (this.loadMode === 'pagination') {
        await this._renderCurrentPage(loadId, state);
      } else {
        await this._renderNextInfiniteBatch(loadId, { reset: true }, state);
      }
    } catch (error) {
      console.error('Error searching collection:', error);
    } finally {
      if (this.searchRequestId === requestId && this.currentLoadId === loadId && this.collectionState === state) {
        this._setLoadingState({ overlay: false, loader: false });
      }
    }
  }

  onUnload() {
    this._disconnectInfiniteObserver();
    if (this.sliderEl && this.sliderEl.swiper) this.sliderEl.swiper.destroy(true, true);
  }

  _initQuantityValidation() {
    this.productGridEl.addEventListener('input', (event) => {
      if (!event.target.matches('.cstm-product-row__qty-input')) return;

      const input = event.target;
      const maxStock = parseInt(input.dataset.max, 10);

      if (maxStock === 0) {
        input.value = 0;
        return;
      }
      
      if (input.value === '') return;

      const currentValue = parseInt(input.value, 10);

      if (isNaN(currentValue)) {
        input.value = 1;
        return;
      }

      if (currentValue > maxStock) {
        input.value = maxStock;
      }
      
      if (currentValue < 1) {
        input.value = 1;
      }
    });
  }

 _initTagToggle() {
    this.productGridEl.addEventListener('click', (event) => {
      const toggleButton = event.target.closest('.cstm-tags-toggle-button');
      if (!toggleButton) return;

      const tagsContainer = toggleButton.closest('.cstm-tags-container');
      if (!tagsContainer) return;
      
      tagsContainer.classList.add('is-expanded');
    });
  }

  _initFilterDrawer() {
    if (!this.filterButton) return;
    this.filterButton.addEventListener('click', () => {
      const drawer = this.filterDrawerContainer.querySelector('#CustomFilterDrawer');
      if (drawer) {
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    });
    this.filterDrawerContainer.addEventListener('click', (event) => {
      const drawer = this.filterDrawerContainer.querySelector('#CustomFilterDrawer');
      if (event.target.closest('[data-drawer-close]') && drawer) {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
      }
      if (event.target.id === 'CustomFilterDrawer-Apply') {
        const form = this.filterDrawerContainer.querySelector('#CustomFacetFiltersForm');
        if (form) {
          this.currentFilters = new URLSearchParams(new FormData(form)).toString();
          if (drawer) drawer.classList.remove('is-open');
          document.body.style.overflow = '';
          this._loadCollection(this.currentCollection, { resetSearch: true, scrollToResults: true });
        }
      }
      if (event.target.id === 'CustomFilterDrawer-Clear') {
        const form = this.filterDrawerContainer.querySelector('#CustomFacetFiltersForm');
        if (form) form.reset();
        if (drawer) drawer.classList.remove('is-open');
        this.currentFilters = '';
        document.body.style.overflow = '';
        this._loadCollection(this.currentCollection, { resetSearch: true, scrollToResults: true });
      }
    });
  }

  async _loadFiltersForCollection(handle) {
    if (!handle) return;
    try {
      const response = await fetch(`/collections/${handle}?view=filters-only`);
      const htmlText = await response.text();
      this.filterDrawerContainer.innerHTML = htmlText;
      if (this.filterButton) this.filterButton.style.display = 'inline-flex';
    } catch(error) {
      console.error("Error fetching filters:", error);
      this.filterDrawerContainer.innerHTML = '';
      if (this.filterButton) this.filterButton.style.display = 'none';
    }
  }

  _initSpecDrawer() {
    document.addEventListener('click', (event) => {
      
      const openTrigger = event.target.closest('[data-drawer-trigger]');
      if (openTrigger) {
        event.preventDefault();
        const drawerId = openTrigger.dataset.drawerTrigger;
        const drawer = document.getElementById(drawerId);

        if (drawer) {
          document.querySelectorAll('.cstm-spec-drawer.is-open').forEach(d => d.classList.remove('is-open'));
          
          drawer.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        }
        return;
      }

      const closeTrigger = event.target.closest('[data-drawer-close]');
      if (closeTrigger) {
        const drawer = closeTrigger.closest('.cstm-spec-drawer');
        if (drawer && drawer.classList.contains('is-open')) {
          drawer.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      }
    });
  }

  _initViewMore() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-view-more-button]');
      if (!button) return;

      const container = button.closest('[data-view-more-container]');
      const shortText = container.querySelector('.short-text');
      const fullText = container.querySelector('.full-text');

      const isShowingShort = shortText.style.display !== 'none';

      if (isShowingShort) {
        shortText.style.display = 'none';
        fullText.style.display = 'inline';
        button.textContent = 'View Less';
      } else {
        shortText.style.display = 'inline';
        fullText.style.display = 'none';
        button.textContent = 'View More';
      }
    });
  }

  _initModelFilterExtras() {
    this.filterDrawerContainer.addEventListener('input', (event) => {
      const searchInput = event.target.closest('[data-model-search]');
      if (!searchInput) return;

      const searchTerm = searchInput.value.trim().toLowerCase();
      const listContainer = searchInput.closest('.cstm-collapsible-filter__content').querySelector('[data-model-list-container]');
      if (!listContainer) return;

      const models = listContainer.querySelectorAll('li[data-model-name]');
      models.forEach(model => {
        const modelName = model.dataset.modelName;
        const isMatch = modelName.includes(searchTerm);
        model.classList.toggle('hidden', !isMatch);
      });
    });

    this.filterDrawerContainer.addEventListener('click', (event) => {
      const viewMoreButton = event.target.closest('[data-view-more-models]');
      if (!viewMoreButton) return;

      const listContainer = viewMoreButton.previousElementSibling;
      if (listContainer && listContainer.matches('[data-model-list-container]')) {
        const isExpanded = listContainer.classList.contains('is-expanded');
        listContainer.classList.toggle('is-expanded');
        viewMoreButton.textContent = isExpanded ? 'View More' : 'View Less';
      }
    });
  }
}

const customShopInstances = {};
function loadCustomShop(container) {
  const sectionId = container.dataset.sectionId;
  if (customShopInstances[sectionId]) customShopInstances[sectionId].onUnload();
  customShopInstances[sectionId] = new CustomShop(container);
}
document.querySelectorAll('[data-section-type="custom-main-shop"]').forEach(container => {
  loadCustomShop(container);
});
document.addEventListener('shopify:section:load', function(event) {
  const container = event.target.querySelector('[data-section-type="custom-main-shop"]');
  if (container) loadCustomShop(container);
});
document.addEventListener('shopify:section:unload', function(event) {
  const container = event.target.querySelector('[data-section-type="custom-main-shop"]');
  if (container) {
    const sectionId = container.dataset.sectionId;
    if (customShopInstances[sectionId]) {
      customShopInstances[sectionId].onUnload();
      delete customShopInstances[sectionId];
    }
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const shopContainer = document.querySelector('[data-section-type="custom-main-shop"]');
  if (!shopContainer) return;

  shopContainer.addEventListener('click', function(event) {
    const trigger = event.target.closest('[data-popup-trigger-for]');
    if (!trigger) return;

    const productId = trigger.dataset.popupTriggerFor;
    const popup = document.querySelector(`[data-popup-id="${productId}"]`);
    
    if (popup) {
      popup.classList.remove('tw-hidden');
      document.body.style.overflow = 'hidden';
    }
  });

  function closePopup(popup) {
    if (!popup) return;
    popup.classList.add('tw-hidden');
    document.body.style.overflow = '';
  }

  shopContainer.addEventListener('click', function(event) {
    const closeButton = event.target.closest('[data-popup-close]');
    const overlay = event.target.closest('[data-popup-overlay]');
    
    if (closeButton || overlay) {
      const popup = event.target.closest('[data-popup-id]');
      closePopup(popup);
    }
  });

  shopContainer.addEventListener('click', function(event) {
    const thumbnail = event.target.closest('[data-popup-thumbnail]');
    if (!thumbnail) return;

    const popup = thumbnail.closest('[data-popup-id]');
    if (!popup) return;

    const mainImage = popup.querySelector('[id^="MainPopupImage-"]');
    const newImageUrl = thumbnail.dataset.fullImageUrl;

    if (mainImage && newImageUrl) {
      mainImage.src = newImageUrl;
      
      const allThumbnails = popup.querySelectorAll('[data-popup-thumbnail]');
      allThumbnails.forEach(th => {
        th.classList.remove('is-active', 'tw-border-blue-500');
        th.classList.add('tw-border-transparent');
      });

      thumbnail.classList.add('is-active');
      thumbnail.classList.remove('tw-border-transparent');
    }
  });

  document.addEventListener('keydown', function(event) {
    if (event.key === "Escape") {
      const openPopup = document.querySelector('[data-popup-id]:not(.tw-hidden)');
      if (openPopup) {
        closePopup(openPopup);
      }
    }
  });
});
